import React from "react";
import { Badge } from "@/components/ui/badge";
import { AnimalAvatar } from "@/components/animals/animal-avatar";

interface AnimalCellProps {
  animalId: string;
  name: string | null;
  species: string | null;
  photoUrl: string | null;
  /** Icon-sized badges (Heart, Flag) — rendered in a flex-nowrap group with the species badge.
   *  These three items will NEVER wrap apart from each other. */
  inlineBadges?: React.ReactNode;
  /** Wide text badges (Outcomed, HOLD) — rendered after the nowrap group and allowed to wrap
   *  onto the following line when the column is narrow. */
  wrapBadges?: React.ReactNode;
  speciesTestId?: string;
}

const AnimalCell = React.forwardRef<HTMLDivElement, AnimalCellProps>(
  ({ animalId, name, species, photoUrl, inlineBadges, wrapBadges, speciesTestId }, ref) => (
    <div ref={ref} className="flex items-center gap-2">
      <AnimalAvatar
        animalId={animalId}
        photoUrl={photoUrl}
        name={name || animalId}
        species={species || ""}
        className="h-7 w-7 shrink-0"
      />
      <div className="min-w-0">
        {/* Row 1: name. When null, renders muted "–". */}
        <div className="font-medium">
          {name ?? <span className="text-muted-foreground">–</span>}
        </div>
        {/* Row 2: AID — always shown, even when name is null. */}
        <div className="text-xs text-muted-foreground font-mono">{animalId}</div>
        {/* Row 3: species badge + inlineBadges in a flex-nowrap shrink-0 group so they can
            never wrap apart from each other; wrapBadges follow outside the group and may
            wrap to the next line when the column is narrow.
            mt-0.5 applied only when species is present. */}
        {(species || inlineBadges || wrapBadges) && (
          <div className={`flex items-center gap-1 flex-wrap${species || inlineBadges ? " mt-0.5" : ""}`}>
            {(species || inlineBadges) && (
              <div className="flex items-center gap-1 flex-nowrap shrink-0">
                {species && (
                  <Badge variant="outline" className="text-xs" data-testid={speciesTestId}>
                    {species.toUpperCase()}
                  </Badge>
                )}
                {inlineBadges}
              </div>
            )}
            {wrapBadges}
          </div>
        )}
      </div>
    </div>
  )
);
AnimalCell.displayName = "AnimalCell";

export { AnimalCell };
